import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FloatingChatButton = () => {
  const { t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  // Prevent body scroll when chat is open on mobile
  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, isMobile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || isLoading) return;
    
    const userMessage = message.trim();
    setMessage("");
    setIsLoading(true);
    
    setChatHistory(prev => [...prev, { type: 'user', text: userMessage }]);
    
    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });
      
      setSessionId(response.data.session_id);
      setChatHistory(prev => [...prev, { type: 'bot', text: response.data.response }]);
    } catch (error) {
      console.error("Error sending message:", error);
      setChatHistory(prev => [...prev, { 
        type: 'bot', 
        text: "Lo siento, hubo un error. Llámanos al (757) 650-3192." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-36 md:bottom-40 right-4 md:right-6 w-12 h-12 md:w-14 md:h-14 bg-[#D4AF37] text-white shadow-xl hover:bg-[#B5952F] hover:shadow-2xl transition-all duration-300 flex items-center justify-center z-50 rounded-full hover:scale-110 ${isOpen ? 'scale-0' : 'scale-100'}`}
        data-testid="floating-chat-button"
        aria-label={t.chat.buttonLabel}
      >
        <MessageCircle className="w-5 h-5 md:w-6 md:h-6" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className={`fixed inset-0 z-50 transition-opacity duration-300 ${
              isMobile ? 'bg-black/20' : 'bg-black/30 backdrop-blur-sm'
            }`}
            onClick={() => setIsOpen(false)}
          />
          
          {/* Mobile: Bottom Sheet */}
          {isMobile ? (
            <div 
              className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl shadow-2xl flex flex-col"
              style={{ 
                maxHeight: '60vh',
                animation: 'slideUp 0.3s ease-out'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag indicator */}
              <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
                <div className="w-10 h-1 bg-slate-300 rounded-full" />
              </div>
              
              {/* Header */}
              <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-[#D4AF37] rounded-full flex items-center justify-center">
                    <MessageCircle className="w-4 h-4" fill="white" strokeWidth={0} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {t.chat.title}
                    </h3>
                    <p className="text-[10px] text-slate-300">{t.chat.description}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                  data-testid="chat-close-button"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Messages Area - Scrollable, takes remaining space */}
              <div 
                className="flex-1 overflow-y-auto p-3 bg-gradient-to-b from-slate-50 to-white min-h-0"
              >
                {chatHistory.length === 0 && (
                  <div className="h-full flex flex-col items-center justify-center text-center px-4 py-6">
                    <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-3">
                      <MessageCircle className="w-7 h-7 text-[#D4AF37]" />
                    </div>
                    <p className="text-slate-500 text-sm">{t.chat.emptyState}</p>
                  </div>
                )}
                <div className="space-y-3">
                  {chatHistory.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${
                          msg.type === 'user'
                            ? 'bg-[#D4AF37] text-white rounded-2xl rounded-br-sm'
                            : 'bg-white text-[#0F172A] shadow-md border border-slate-100 rounded-2xl rounded-bl-sm'
                        }`}
                      >
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white text-slate-400 px-3 py-2 rounded-2xl rounded-bl-sm shadow-md border border-slate-100 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-xs">Escribiendo...</span>
                      </div>
                    </div>
                  )}
                </div>
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area - Always visible at bottom */}
              <form onSubmit={handleSubmit} className="p-3 bg-white border-t border-slate-100 flex-shrink-0">
                <div className="flex gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t.chat.inputPlaceholder}
                    disabled={isLoading}
                    className="flex-1 px-4 py-3 text-sm bg-slate-50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37] disabled:bg-slate-100 placeholder:text-slate-400"
                    data-testid="chat-input"
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !message.trim()}
                    className="w-11 h-11 bg-[#D4AF37] text-white rounded-xl flex items-center justify-center hover:bg-[#B5952F] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="chat-send-button"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* Desktop: Centered Modal - unchanged behavior */
            <div 
              className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
              style={{ animation: 'zoomIn 0.2s ease-out' }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center">
                    <MessageCircle className="w-5 h-5" fill="white" strokeWidth={0} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {t.chat.title}
                    </h3>
                    <p className="text-xs text-slate-300">{t.chat.description}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors"
                  data-testid="chat-close-button"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Messages Area */}
              <div className="h-80 overflow-y-auto p-4 bg-gradient-to-b from-slate-50 to-white">
                {chatHistory.length === 0 && (
                  <div className="h-full flex flex-col items-center justify-center text-center px-6">
                    <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-4">
                      <MessageCircle className="w-8 h-8 text-[#D4AF37]" />
                    </div>
                    <p className="text-slate-500 text-sm">{t.chat.emptyState}</p>
                  </div>
                )}
                <div className="space-y-4">
                  {chatHistory.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                          msg.type === 'user'
                            ? 'bg-[#D4AF37] text-white rounded-2xl rounded-br-sm'
                            : 'bg-white text-[#0F172A] shadow-md border border-slate-100 rounded-2xl rounded-bl-sm'
                        }`}
                      >
                        {msg.text}
                      </div>
                    </div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white text-slate-400 px-4 py-3 rounded-2xl rounded-bl-sm shadow-md border border-slate-100 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-xs">Escribiendo...</span>
                      </div>
                    </div>
                  )}
                </div>
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <form onSubmit={handleSubmit} className="p-4 bg-white border-t border-slate-100">
                <div className="flex gap-3">
                  <input
                    ref={inputRef}
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={t.chat.inputPlaceholder}
                    disabled={isLoading}
                    className="flex-1 px-4 py-3 text-sm bg-slate-50 border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#D4AF37] disabled:bg-slate-100 placeholder:text-slate-400"
                    data-testid="chat-input"
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !message.trim()}
                    className="w-12 h-12 bg-[#D4AF37] text-white rounded-xl flex items-center justify-center hover:bg-[#B5952F] transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
                    data-testid="chat-send-button"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}

      {/* CSS for animations */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes zoomIn {
          from {
            transform: translate(-50%, -50%) scale(0.95);
            opacity: 0;
          }
          to {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
};

export default FloatingChatButton;
